import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "lib/classNames";
import { CartContext } from "components/cart";
import styled, { createGlobalStyle } from "styled-components";

const CustomSelect = styled.select`
  border-color: ${(props) => props.secondary}!important;
  background: ${(props) => props.secondary};
  &:focus {
    background: ${(props) => props.background};
  }
`;

const VariantsPicker = ({
  CustomScroll,
  product,
  parentRef,
  onConfirm,
  buttonText
}) => {
  const { options, variants, title } = product;
  const {
    cart_settings: {
      theme: { background, secondary },
      modules: { checkout_button },
      product: { sold_out },
      rtl,
      money_format
    },
    Utils,
    setVariantsPickerShow,
    rechargeSettings,
    hasWriteDiscountsScope
  } = useContext(CartContext);

  const subscriptionObject = rechargeSettings?.products.find((p) => {
    return Object.keys(p)[0] === product.id.toString();
  });
  const subscription = subscriptionObject && subscriptionObject[product.id];
  const [selectedOptions, setSelectedOptions] = useState(
    options.reduce((acc, cur) => {
      return (acc = Object.assign(acc, { [cur.name]: cur.values[0] }));
    }, {})
  );
  const getSelectedVariantName = (selectedOptions) => {
    return Object.values(selectedOptions).join(" / ");
  };
  const [selectedVariant, setSelectedVariant] = useState(
    variants.find(
      ({ title }) => title === getSelectedVariantName(selectedOptions)
    )
  );
  const [sellingPlan, setSellingPlan] = useState(null);

  const onChange = (name, value) =>
    setSelectedOptions(Object.assign({}, selectedOptions, { [name]: value }));

  useEffect(() => {
    const variant = variants.find(
      ({ title }) => title === getSelectedVariantName(selectedOptions)
    );
    setSelectedVariant(variant);
  }, [selectedOptions]);

  let variantImage;
  if (selectedVariant && selectedVariant.image) {
    variantImage = selectedVariant.image;
  } else if (product.image && product.image.src) {
    variantImage = product.image.src;
  } else {
    variantImage = product.image;
  }
  const HAS_SUBSCRIPTION =
    subscription &&
    !!subscription?.selling_plan_groups?.length &&
    rechargeSettings?.widget_settings?.published &&
    hasWriteDiscountsScope;

  const out_of_stock =
    !selectedVariant ||
    (selectedVariant.inventory_management &&
      selectedVariant.inventory_quantity <= 0 &&
      selectedVariant.inventory_policy === "deny");
  return (
    <div
      className={classNames("px-5 py-3 flex flex-col max-h-full")}
      style={{
        background: Utils.formatColor(background),
        paddingBottom: "60px"
      }}
    >
      <div className={classNames("flex justify-end pb-3")}>
        <svg
          aria-hidden="true"
          focusable="false"
          data-icon="times"
          role="img"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 320 512"
          className={classNames("w-3 h-3 cursor-pointer")}
          onClick={() => setVariantsPickerShow(false)}
        >
          <path
            fill="currentColor"
            d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"
          ></path>
        </svg>
      </div>
      <div className={classNames("flex pb-3")}>
        <div
          className={classNames(
            "bg-cover bg-center w-20 h-20 rounded",
            rtl ? "ml-2" : "mr-2"
          )}
          style={{
            backgroundImage: `url(${variantImage})`
          }}
        />
        <div className={classNames("flex-1")}>
          <b
            className={classNames("block font-bold mb-2 overflow-hidden")}
            style={{
              fontSize: Utils.em(1),
              lineHeight: Utils.em(1 + 0.2),
              maxHeight: Utils.em(2 + 0.4)
            }}
          >
            {title}
          </b>
          {!!HAS_SUBSCRIPTION && sellingPlan && (
            <>
              <div className={classNames("flex items-center")}>
                <p
                  className={classNames("flex-1")}
                  dangerouslySetInnerHTML={{
                    __html: Utils.formatMoney(
                      parseFloat(sellingPlan.variant.prices.discounted_price) *
                      100,
                      money_format
                    )
                  }}
                />
                <div
                  className={classNames(
                    "relative flex flex-col items-center recharge-tooltip"
                  )}
                >
                  <svg
                    width="18"
                    height="21"
                    viewBox="0 0 356 418"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    className={classNames(
                      "h-4 w-4 inline-block cursor-pointer",
                      rtl ? "ml-2" : "mr-2"
                    )}
                  >
                    <g id="black">
                      <path
                        id="Fill 17"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M208.139 100.679C178.483 116.157 148.911 131.595 119.047 147.181C128.329 152.441 137.201 157.467 146.065 162.489C174.241 145.793 202.247 129.197 230.541 112.429C222.927 108.435 215.611 104.597 208.139 100.679ZM296.785 147.109C283.563 140.185 270.605 133.399 257.683 126.633C229.173 143.531 200.789 160.351 172.175 177.309C184.385 184.231 196.243 190.953 208.055 197.647C237.691 180.767 267.139 163.995 296.785 147.109ZM199.859 211.585C187.995 204.861 176.163 198.155 163.963 191.239V227.969C155.117 222.959 146.567 218.113 137.989 213.255V176.505C128.823 171.305 119.683 166.123 110.513 160.921V267.259C140.371 284.039 170.015 300.697 199.859 317.467V211.585ZM305.683 266.883V160.647C275.571 177.795 245.815 194.741 216.209 211.603V318.173C246.269 300.941 276.093 283.845 305.683 266.883ZM94.345 141.839C132.143 122.111 170.093 102.305 208.147 82.4433C245.963 102.249 283.771 122.049 321.819 141.975V276.261C284.405 297.711 246.667 319.345 208.819 341.043C170.725 319.635 132.641 298.233 94.345 276.713V141.839Z"
                      ></path>
                      <path
                        id="Fill 19"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M320.976 35.1783C317.376 26.5083 313.918 18.1863 310.364 9.62626C315.912 7.31226 321.392 5.02626 327.114 2.64026C336.144 24.3503 345.114 45.9143 354.154 67.6443C331.33 69.5583 308.78 71.4503 285.934 73.3663C285.438 67.3863 284.946 61.4703 284.434 55.2903C294.314 54.4503 303.99 53.6303 313.664 52.8083C289.23 35.8543 262.294 25.3283 232.896 21.7323C179.362 15.1843 130.966 28.2523 88.89 62.1463C5.96599 128.946 -4.66001 248.868 61.006 329.478C129.006 412.956 257.026 424.866 339.242 346.33C343.386 350.64 347.53 354.952 351.676 359.264C284.342 425.486 170.696 440.052 84.742 376.82C-2.73201 312.468 -26.02 191.732 31.602 99.8823C62.276 50.9823 106.16 19.6043 162.564 7.01826C218.896 -5.55174 271.668 4.54026 320.976 35.1783Z"
                      ></path>
                    </g>
                  </svg>
                  <div
                    className={classNames(
                      "absolute top-0 flex flex-col hidden mt-6 recharge-tooltip-content",
                      rtl ? "left-0" : "right-0"
                    )}
                  >
                    <div
                      className={classNames(
                        "w-3 h-3 -mb-2 rotate-45 bg-black recharge-tooltip-caret"
                      )}
                    />
                    <span
                      className={classNames(
                        "relative z-10 p-2 text-xs text-white rounded bg-black shadow-lg"
                      )}
                      dangerouslySetInnerHTML={{
                        __html: rechargeSettings?.widget_settings?.how_it_works
                      }}
                    />
                  </div>
                </div>
              </div>
              <p className={classNames("text-xs")}>
                {sellingPlan.selling_plan_name}
              </p>
            </>
          )}{" "}
          {(!HAS_SUBSCRIPTION || !sellingPlan) && (
            <p
              dangerouslySetInnerHTML={{
                __html: Utils.formatMoney(
                  parseFloat(
                    selectedVariant ? selectedVariant.price : variants[0].price
                  ) * 100,
                  money_format
                )
              }}
            />
          )}
        </div>
      </div>

      <CustomScroll
        className={classNames("flex-1")}
        thumbBg={Utils.formatColor(secondary)}
        trackBg={Utils.formatColor(background)}
        isMobile={Utils.isMobile()}
      >
        {HAS_SUBSCRIPTION && (
          <Recharge
            subscription={subscription}
            setSellingPlan={setSellingPlan}
            background={Utils.formatColor(secondary)}
            widget_settings={rechargeSettings.widget_settings}
            selected_variant_id={selectedVariant?.id}
            formatMoney={(v) => Utils.formatMoney(v * 100, money_format)}
          />
        )}

        {!Utils.productHasOneVariant({ variants }) &&
          // (options.length != 1) &&
          options.map((option, key) => {
            return (
              <div className={classNames("pb-2")} key={key}>
                <label
                  className={classNames(
                    "block capitalize text-sm font-bold mb-1"
                  )}
                >
                  {option.name}
                </label>
                <div className={classNames("relative mb-2")}>
                  <CustomSelect
                    background={Utils.formatColor(background)}
                    secondary={Utils.formatColor(secondary)}
                    className={classNames(
                      "block appearance-none w-full m-0 border-2 py-3 px-4 pr-8 rounded leading-tight focus:outline-none h-auto"
                    )}
                    onChange={({ target }) => {
                      onChange(option.name, target.value);
                    }}
                  >
                    {option.values.map((value) => {
                      return (
                        <option value={value} key={option.name + value}>
                          {value}
                        </option>
                      );
                    })}
                  </CustomSelect>
                  <div
                    className={classNames(
                      "pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
                    )}
                  >
                    <svg
                      className={classNames("fill-current h-4 w-4")}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
            );
          })}
        <button
          className={classNames("custom-btn w-full rounded w-full p-4 mb-3", {
            "opacity-50 cursor-not-allowed": out_of_stock
          })}
          style={{
            backgroundColor: Utils.formatColor(checkout_button.background),
            borderColor: Utils.formatColor(checkout_button.background),
            color: Utils.formatColor(checkout_button.button_text.color)
          }}
          onClick={() =>
            onConfirm(selectedVariant, sellingPlan?.selling_plan_id)
          }
          disabled={out_of_stock}
        >
          {out_of_stock ? sold_out : buttonText}
        </button>
      </CustomScroll>
    </div>
  );
};

VariantsPicker.propTypes = {};

export default VariantsPicker;

function Recharge ({
  subscription,
  widget_settings,
  selected_variant_id,
  background,
  setSellingPlan,
  formatMoney
}) {
  const {
    subscribe_message,
    subscribe_without_discount_message,
    onetime_message,
    select_subscription_first
  } = widget_settings;
  const {
    subscription_options,
    variants,
    external_product_id,
    selling_plan_groups
  } = subscription;
  const one_time_is_allowed =
    subscription_options.storefront_purchase_options ===
    "subscription_and_onetime";
  const [value, setValue] = useState(
    select_subscription_first === "true" || !one_time_is_allowed
      ? "subscription"
      : "one_time"
  );
  const selected_variant = variants.find((v) => v.id === selected_variant_id);
  const { unit_price, discounted_price } = selected_variant.prices;
  const has_discount = unit_price !== discounted_price;
  const price_markup = has_discount
    ? `<del class="recharge-del-opacity mu-mx-1">${formatMoney(
      unit_price
    )}</del>  ${formatMoney(discounted_price)}`
    : formatMoney(unit_price);
  const subscribe_label = has_discount
    ? subscribe_message
    : subscribe_without_discount_message;
  const options = [
    one_time_is_allowed && {
      label: onetime_message,
      value: "one_time"
    },
    {
      label: subscribe_label,
      info: price_markup,
      value: "subscription"
    }
  ].filter(Boolean);
  const handleChange = (event) => {
    setValue(event.currentTarget.value);
  };

  useEffect(() => {
    if (
      value === "one_time" ||
      !selected_variant?.selling_plan_allocations.length
    ) {
      setSellingPlan(null);
      return;
    }
    const { selling_plan_allocations } = selected_variant;
    const { selling_plan_group_id, selling_plan_id } =
      selling_plan_allocations[0];
    const group = selling_plan_groups.find(
      (g) => g.selling_plan_group_id === selling_plan_group_id
    );
    if (!group) {
      setSellingPlan(null);
      return;
    }
    const selling_plan = group.selling_plans.find(
      (p) => p.selling_plan_id === selling_plan_id
    );
    setSellingPlan({ ...selling_plan, variant: selected_variant });
  }, [selected_variant, value]);
  return (
    <div className={classNames("pb-2 ")}>
      {options.map((option) => (
        <div
          key={`${option.value}_${external_product_id}`}
          className={classNames("my-1 rounded flex items-center")}
          style={{ background }}
        >
          <input
            type="radio"
            id={`${option.value}_${external_product_id}`}
            name={`recharge_${external_product_id}`}
            value={option.value}
            checked={option.value === value}
            onChange={handleChange}
            className={classNames("mx-2")}
          />
          <label
            htmlFor={`${option.value}_${external_product_id}`}
            className={classNames("flex-1 flex p-2 h-full")}
          >
            <span
              className={classNames("flex-1")}
              dangerouslySetInnerHTML={{ __html: option.label }}
            />
            {option.info && (
              <b dangerouslySetInnerHTML={{ __html: option.info }} />
            )}
          </label>
        </div>
      ))}
    </div>
  );
}

function SubscriptionAndOnetime ({ subscription }) { }
function SubscriptionOnly ({ subscription }) { }
